import { FineResponse } from 'utils/types';
import { AxiosResponse } from 'axios';
import { axiosInstance } from 'hooks/auth/AxiosInstance';
import { UseQueryResult, useQuery } from '@tanstack/react-query';

type PageType = {
  pageSize: string;
  pageNumber: string;
  filters?: Record<string, string>;
  enabled?: boolean;
  refetchInterval?: number;
};

type Options = {
  options: PageType;
};

const fetchData = async ({
  options: { pageNumber, pageSize, filters = {} },
}: Options): Promise<FineResponse> => {
  const queryParams = new URLSearchParams({
    ...filters,
    pageNumber: pageNumber,
    pageSize,
  }).toString();

  const response: AxiosResponse<FineResponse> = await axiosInstance.get(
    `reports/filter_param?${queryParams}`,
  );
  return response.data;
};

const useGetFines = ({
  options,
}: Options): UseQueryResult<FineResponse, Error> => {
  // ✅ Resolve defaults here so the same object is used in both
  //    queryKey and queryFn — no stale closure mismatch
  const resolvedOptions: PageType = {
    pageNumber: options.pageNumber ?? '0',
    pageSize: options.pageSize ?? '10',
    filters: options.filters ?? {},
    enabled: options.enabled,
    refetchInterval: options.refetchInterval ?? 10000,
  };

  return useQuery<FineResponse, Error>({
    queryKey: [
      'fines',
      resolvedOptions.pageNumber,
      resolvedOptions.pageSize,
      resolvedOptions.filters,
    ],
    queryFn: () => fetchData({ options: resolvedOptions }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    enabled: resolvedOptions.enabled,
    refetchInterval: resolvedOptions.refetchInterval,
  });
};

export default useGetFines;
